import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * Markdown formatting utility for safe conversion of Markdown to HTML
 * Supports basic formatting only (bold, italic, links, lists, code)
 * No script tags or dangerous HTML allowed
 */
export class MarkdownFormatter {
  private static instance: MarkdownFormatter;

  private constructor() {
    // Configure marked with secure defaults
    marked.setOptions({
      breaks: true, // Convert line breaks to <br>
      gfm: true, // GitHub Flavored Markdown
    });

    // Use hooks to customize rendering
    marked.use({
      hooks: {
        preprocess(markdown: string) {
          return markdown;
        }
      },
      renderer: {
        // Override heading rendering to limit heading sizes for comments
        heading({ text, depth }: { text: string; depth: number }) {
          // Map markdown headings to h3-h6 (skip h1-h2 which are too large for comments)
          const level = Math.max(3, Math.min(6, depth + 2)); // h3, h4, h5, h6 only
          return `<h${level}>${text}</h${level}>\n`;
        },
        // Override image rendering - disable images for security
        image() {
          return '[Image not supported]';
        },
        // Override HTML rendering - strip all HTML
        html() {
          return '';
        }
      }
    });
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): MarkdownFormatter {
    if (!MarkdownFormatter.instance) {
      MarkdownFormatter.instance = new MarkdownFormatter();
    }
    return MarkdownFormatter.instance;
  }

  /**
   * Convert Markdown text to safe HTML
   * @param markdown - Raw Markdown text
   * @returns Sanitized HTML string
   */
  public toHtml(markdown: string): string {
    if (!markdown || markdown.trim() === '') {
      return '';
    }

    try {
      // Convert Markdown to HTML (configuration already applied via marked.use())
      const rawHtml = marked(markdown) as string;

      // Sanitize HTML to remove any dangerous content
      const cleanHtml = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'b', 'i', 'u',
          'a', 'ul', 'ol', 'li', 'code', 'pre',
          'blockquote', 'h3', 'h4', 'h5', 'h6'
        ],
        ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
        ALLOW_DATA_ATTR: false,
        ALLOW_UNKNOWN_PROTOCOLS: false,
        SAFE_FOR_TEMPLATES: true,
      });

      return cleanHtml;
    } catch (error) {
      console.error('Error parsing Markdown:', error);
      // Return escaped plain text as fallback
      return this.escapeHtml(markdown);
    }
  }

  /**
   * Escape HTML entities in plain text
   * @param text - Plain text
   * @returns Escaped HTML string
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Insert Markdown formatting at cursor position
   * @param textarea - Textarea element
   * @param before - Text to insert before selection
   * @param after - Text to insert after selection
   * @param placeholder - Placeholder text if nothing is selected
   */
  public insertFormatting(
    textarea: HTMLTextAreaElement,
    before: string,
    after: string = '',
    placeholder: string = 'text'
  ): void {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newText =
      textarea.value.substring(0, start) +
      before +
      textToInsert +
      after +
      textarea.value.substring(end);

    textarea.value = newText;

    // Set cursor position
    const newCursorPos = start + before.length + textToInsert.length;
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    textarea.focus();

    // Trigger input event to update character count
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Format helpers for common Markdown patterns
   */
  public static formatBold(textarea: HTMLTextAreaElement): void {
    MarkdownFormatter.getInstance().insertFormatting(textarea, '**', '**', 'bold text');
  }

  public static formatItalic(textarea: HTMLTextAreaElement): void {
    MarkdownFormatter.getInstance().insertFormatting(textarea, '_', '_', 'italic text');
  }

  public static formatLink(textarea: HTMLTextAreaElement): void {
    const selectedText = textarea.value.substring(textarea.selectionStart, textarea.selectionEnd);
    
    if (selectedText) {
      MarkdownFormatter.getInstance().insertFormatting(
        textarea,
        '[',
        '](https://example.com)',
        'link text'
      );
    } else {
      MarkdownFormatter.getInstance().insertFormatting(
        textarea,
        '[link text](',
        ')',
        'https://example.com'
      );
    }
  }

  public static formatUnorderedList(textarea: HTMLTextAreaElement): void {
    const start = textarea.selectionStart;
    const value = textarea.value;
    
    // Find the start of the current line
    let lineStart = start;
    while (lineStart > 0 && value[lineStart - 1] !== '\n') {
      lineStart--;
    }

    // Insert bullet point at line start
    const newText = value.substring(0, lineStart) + '- ' + value.substring(lineStart);
    textarea.value = newText;
    textarea.setSelectionRange(start + 2, start + 2);
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  public static formatOrderedList(textarea: HTMLTextAreaElement): void {
    const start = textarea.selectionStart;
    const value = textarea.value;
    
    // Find the start of the current line
    let lineStart = start;
    while (lineStart > 0 && value[lineStart - 1] !== '\n') {
      lineStart--;
    }

    // Insert numbered list at line start
    const newText = value.substring(0, lineStart) + '1. ' + value.substring(lineStart);
    textarea.value = newText;
    textarea.setSelectionRange(start + 3, start + 3);
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  public static formatCode(textarea: HTMLTextAreaElement): void {
    MarkdownFormatter.getInstance().insertFormatting(textarea, '`', '`', 'code');
  }

  public static formatHeading(textarea: HTMLTextAreaElement): void {
    const start = textarea.selectionStart;
    const value = textarea.value;
    
    // Find the start of the current line
    let lineStart = start;
    while (lineStart > 0 && value[lineStart - 1] !== '\n') {
      lineStart--;
    }

    // Check if line already has a heading marker
    const lineContent = value.substring(lineStart, start);
    const headingMatch = lineContent.match(/^(#{1,6})\s/);
    
    if (headingMatch) {
      // Already has heading, increase level or remove if at max
      const currentLevel = headingMatch[1].length;
      if (currentLevel < 6) {
        // Increase heading level
        const newText = value.substring(0, lineStart) + '#' + value.substring(lineStart);
        textarea.value = newText;
        textarea.setSelectionRange(start + 1, start + 1);
      } else {
        // Remove heading markers
        const newText = value.substring(0, lineStart) + 
                       value.substring(lineStart).replace(/^#{1,6}\s/, '');
        textarea.value = newText;
        textarea.setSelectionRange(start - headingMatch[0].length, start - headingMatch[0].length);
      }
    } else {
      // Add heading marker (start with #, which becomes h3 after transformation)
      const newText = value.substring(0, lineStart) + '# ' + value.substring(lineStart);
      textarea.value = newText;
      textarea.setSelectionRange(start + 2, start + 2);
    }
    
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
